import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getPendingBilling, markAsBilled, unmarkBilled } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Loader2, Search, DollarSign, CheckCircle2, Clock,
} from "lucide-react";

const PRODUCT_LABELS = {
  marketing: "Marketing", web_design: "Web Design", seo_aeo: "SEO/AEO",
  prospect_tools: "ProspectID/Max",
};

export default function PendingBillingPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("pending");

  const fetchOrders = async () => {
    try {
      const { data } = await getPendingBilling();
      setOrders(data);
    } catch (e) {
      console.error("Failed to fetch billing:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  const handleMarkBilled = async (projectId) => {
    try {
      await markAsBilled(projectId);
      toast.success("Marked as billed");
      fetchOrders();
    } catch (e) {
      toast.error("Failed to update billing");
    }
  };

  const handleUnbill = async (projectId) => {
    try {
      await unmarkBilled(projectId);
      toast.success("Billing status reverted");
      fetchOrders();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  const filtered = orders.filter((o) =>
    o.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.client_company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.agency_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const pendingOrders = filtered.filter((o) => o.billing_status !== "billed");
  const billedOrders = filtered.filter((o) => o.billing_status === "billed");

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div>
        <h1 className="font-outfit text-3xl font-bold tracking-tight">Pending Billing</h1>
        <p className="text-muted-foreground mt-1">
          {pendingOrders.length} orders awaiting billing
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search by client, company, or agency..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="search-billing" />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending-billing">
            <Clock className="h-4 w-4 mr-2" />Pending ({pendingOrders.length})
          </TabsTrigger>
          <TabsTrigger value="billed" data-testid="tab-billed">
            <CheckCircle2 className="h-4 w-4 mr-2" />Billed ({billedOrders.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          {pendingOrders.length === 0 ? (
            <Card className="border"><CardContent className="flex flex-col items-center justify-center py-16">
              <DollarSign className="h-16 w-16 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium">All caught up!</h3>
              <p className="text-muted-foreground text-sm">No orders pending billing</p>
            </CardContent></Card>
          ) : (
            <Card className="border"><CardContent className="pt-6">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="w-[50px]">Bill</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Agency</TableHead>
                  <TableHead>Products</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Rep</TableHead>
                  <TableHead>Order Date</TableHead>
                  <TableHead className="w-[140px]"></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {pendingOrders.map((order) => (
                    <TableRow key={order.id} className="table-row-hover">
                      <TableCell>
                        <Checkbox
                          onCheckedChange={() => handleMarkBilled(order.id)}
                          data-testid={`bill-${order.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <Link
                            to={`/orders/${order.id}`}
                            className="font-medium text-primary hover:underline"
                            data-testid={`pending-bill-client-${order.id}`}
                          >
                            {order.client_name}
                          </Link>
                          {order.client_company && <p className="text-xs text-muted-foreground">{order.client_company}</p>}
                          {order.client_email && <p className="text-xs text-muted-foreground">{order.client_email}</p>}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{order.agency_name}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {order.product_names.map((name, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{name}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-muted-foreground space-y-0.5">
                          {order.sub_products?.length > 0 && (
                            <p>{order.sub_products.join(", ")}</p>
                          )}
                          {order.channel_quantities && Object.entries(order.channel_quantities).map(([ch, qty]) => (
                            <p key={ch}>{ch}: {Object.values(qty).join(", ")}</p>
                          ))}
                          {order.start_date && <p>{order.start_date}{order.end_date ? ` to ${order.end_date}` : ""}</p>}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{order.rep_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDate(order.created_at)}</TableCell>
                      <TableCell>
                        <Button asChild variant="ghost" size="sm" data-testid={`pending-bill-view-${order.id}`}>
                          <Link to={`/orders/${order.id}`}>View Order →</Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="billed" className="mt-4">
          {billedOrders.length === 0 ? (
            <Card className="border"><CardContent className="flex flex-col items-center justify-center py-16">
              <p className="text-muted-foreground text-sm">No billed orders yet</p>
            </CardContent></Card>
          ) : (
            <Card className="border"><CardContent className="pt-6">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="w-[50px]">Undo</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Agency</TableHead>
                  <TableHead>Products</TableHead>
                  <TableHead>Rep</TableHead>
                  <TableHead>Order Date</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {billedOrders.map((order) => (
                    <TableRow key={order.id} className="table-row-hover opacity-70">
                      <TableCell>
                        <Checkbox checked={true} onCheckedChange={() => handleUnbill(order.id)} data-testid={`unbill-${order.id}`} />
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/orders/${order.id}`}
                          className="font-medium text-primary hover:underline"
                        >
                          {order.client_name}
                        </Link>
                        {order.client_company && <p className="text-xs text-muted-foreground">{order.client_company}</p>}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{order.agency_name}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {order.product_names.map((name, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{name}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{order.rep_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDate(order.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
